export const ROLE_MEMBER = 'Member';
export const ROLE_TESTER = 'Tester';
export const ROLE_ADMINISTRATOR = 'Administrator';
export const ROLE_CONTENT_PROCESSOR_SERVICE = 'ContentProcessorService';
export const ROLE_SUPER_ADMINISTRATOR = 'SuperAdministrator';

export const ADMIN_USER_ACCESS_ROLES = [
    ROLE_MEMBER,
    ROLE_TESTER,
    ROLE_ADMINISTRATOR,
    ROLE_SUPER_ADMINISTRATOR,
    ROLE_CONTENT_PROCESSOR_SERVICE
] as const;

export type AdminUserAccessRole = typeof ADMIN_USER_ACCESS_ROLES[number];

const adminUserAccessRoleNames = new Set<string>(ADMIN_USER_ACCESS_ROLES);

export const isAdminUserAccessRole = (roleName: string): roleName is AdminUserAccessRole =>
    adminUserAccessRoleNames.has(roleName);

export const getAdminUserAccessRole = (roles: string[]): AdminUserAccessRole => {
    const elevatedRole = [
        ROLE_SUPER_ADMINISTRATOR,
        ROLE_CONTENT_PROCESSOR_SERVICE,
        ROLE_ADMINISTRATOR,
        ROLE_TESTER
    ].find((roleName) => roles.includes(roleName));
    return (elevatedRole as AdminUserAccessRole | undefined) ?? ROLE_MEMBER;
};

export const buildAdminUserRolesForAccess = (
    currentRoles: string[],
    accessRole: AdminUserAccessRole
): string[] => {
    const preservedRoles = currentRoles.filter((roleName) => !isAdminUserAccessRole(roleName));
    return Array.from(new Set([
        ROLE_MEMBER,
        ...preservedRoles,
        ...(accessRole === ROLE_MEMBER ? [] : [accessRole])
    ]));
};

export const hasRole = (roles: string[] | undefined, roleName: string): boolean => {
    if (!roles || roles.length === 0) {
        return false;
    }

    return roles.includes(roleName);
};

export const hasAnyRole = (roles: string[] | undefined, roleNames: string[]): boolean => {
    if (!roles || roles.length === 0 || roleNames.length === 0) {
        return false;
    }

    return roles.some((role) => roleNames.includes(role));
};

export const isAdmin = (roles: string[] | undefined): boolean =>
    hasAnyRole(roles, [ROLE_ADMINISTRATOR, ROLE_SUPER_ADMINISTRATOR]);

export const isSuperAdmin = (roles: string[] | undefined): boolean =>
    hasRole(roles, ROLE_SUPER_ADMINISTRATOR);
